/**
 * Render the assistant's markdown and inline proposal previews.
 *
 * Deliberately not a full markdown library. The agent writes a narrow, known subset —
 * headings, bold, italics, underline, links, inline code, bullet and numbered lists — and handling exactly
 * that keeps a dependency out of the bundle and avoids `dangerouslySetInnerHTML`, since
 * everything here becomes React elements rather than HTML.
 *
 * Inline code renders LTR: identifiers and block ids are Latin inside otherwise RTL prose.
 */

import { Box, Link, Typography } from '@mui/material'
import React, { useMemo } from 'react'

/** Match outer links/marks first, then render their labels recursively. Code stays literal. */
const INLINE = /(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|<u>[^<]+<\/u>)/g

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  return text.split(INLINE).map((part, index) => {
    const key = `${keyPrefix}-${index}`
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(part)
    if (link) {
      const href = /^(https?:|mailto:|\/|#)/i.test(link[2]) ? link[2] : undefined
      return (
        <Link key={key} href={href} target="_blank" rel="noopener noreferrer">
          {renderInline(link[1], key)}
        </Link>
      )
    }
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={key}>{renderInline(part.slice(2, -2), key)}</strong>
    }
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      return (
        <Box
          key={key}
          component="code"
          dir="ltr"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.85em',
            px: 0.5,
            borderRadius: 0.5,
            bgcolor: 'action.hover',
            unicodeBidi: 'embed',
          }}
        >
          {part.slice(1, -1)}
        </Box>
      )
    }
    if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      return <em key={key}>{renderInline(part.slice(1, -1), key)}</em>
    }
    if (part.startsWith('<u>') && part.endsWith('</u>')) {
      return <u key={key}>{renderInline(part.slice(3, -4), key)}</u>
    }
    return <React.Fragment key={key}>{part}</React.Fragment>
  })
}

type Line =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'item'; ordered: boolean; marker: string; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'blank' }

function classify(line: string): Line {
  const trimmed = line.trim()
  if (!trimmed) {
    return { kind: 'blank' }
  }

  const heading = /^(#{1,6})\s+(.*)$/.exec(trimmed)
  if (heading) {
    return { kind: 'heading', level: heading[1].length, text: heading[2] }
  }

  const bullet = /^[-*]\s+(.*)$/.exec(trimmed)
  if (bullet) {
    return { kind: 'item', ordered: false, marker: '•', text: bullet[1] }
  }

  const numbered = /^(\d+)[.)]\s+(.*)$/.exec(trimmed)
  if (numbered) {
    return { kind: 'item', ordered: true, marker: `${numbered[1]}.`, text: numbered[2] }
  }

  return { kind: 'paragraph', text: trimmed }
}

export interface MarkdownTextProps {
  children: string
}

const MarkdownText: React.FC<MarkdownTextProps> = ({ children }) => {
  const lines = useMemo(() => children.split('\n').map(classify), [children])

  return (
    <Box sx={{ '& > *:first-of-type': { mt: 0 } }}>
      {lines.map((line, index) => {
        if (line.kind === 'blank') {
          return null
        }

        if (line.kind === 'heading') {
          return (
            <Typography
              key={index}
              sx={{
                mt: 1.5,
                mb: 0.5,
                fontWeight: 700,
                // Heading levels are close together on purpose: this is a chat reply, not a
                // document, and full heading scale would shout inside a narrow pane.
                fontSize: line.level <= 2 ? '0.95rem' : '0.88rem',
              }}
            >
              {renderInline(line.text, `h${index}`)}
            </Typography>
          )
        }

        if (line.kind === 'item') {
          return (
            <Box key={index} sx={{ display: 'flex', gap: 0.75, my: 0.25 }}>
              <Typography variant="body2" sx={{ opacity: 0.6, flexShrink: 0 }}>
                {line.marker}
              </Typography>
              <Typography variant="body2">{renderInline(line.text, `i${index}`)}</Typography>
            </Box>
          )
        }

        return (
          <Typography key={index} variant="body2" sx={{ my: 0.5 }}>
            {renderInline(line.text, `p${index}`)}
          </Typography>
        )
      })}
    </Box>
  )
}

export default MarkdownText
